import {
  DollarSign,
  Package,
  Star,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { useAppSelector } from "@/hooks/redux";
import api from "@/services/api";

interface BusinessAnalyticsData {
  averageDeliveryTime?: number;
  averageOrderValue?: number;
  averageRating?: number;
  completedOrders?: number;
  customerRetention?: number;
  driverEfficiency?: number;
  fleetUtilization?: number;
  monthlyRevenue: number[];
  ordersByHour: number[];
  pieCharts?: PieChart[];
  successRate?: number;
  topProducts: {
    name: string;
    orders: number;
    revenue: number;
  }[];
  totalOrders: number;
  totalRevenue: number;
}

interface PieChart {
  centerValue: string;
  segments: PieSegment[];
  title: string;
}

interface PieSegment {
  color: string;
  label: string;
  percentage: number;
}

const BusinessAnalytics: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [timeRange, setTimeRange] = useState<"month" | "week" | "year">(
    "month",
  );
  const [analyticsData, setAnalyticsData] =
    useState<BusinessAnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/business/analytics/comprehensive`);
        if (response?.data?.success) {
          // API returns role-specific shapes. Accept as-is and rely on optional fields.
          setAnalyticsData(response.data.data as BusinessAnalyticsData);
        }
      } catch (err) {
        setError("Failed to load analytics data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeRange]);

  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `R${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R${(value / 1000).toFixed(1)}k`;
    }
    return `R${value.toFixed(0)}`;
  };

  const getBusinessTypeMetrics = () => {
    if (!analyticsData) return [];

    const baseMetrics = [
      {
        change: "+12.5%",
        changeType: "positive" as const,
        icon: DollarSign,
        title: "Total Revenue",
        value: formatCurrency(analyticsData.totalRevenue),
      },
      {
        change: "+8.2%",
        changeType: "positive" as const,
        icon: Package,
        title: "Total Orders",
        value: analyticsData.totalOrders.toLocaleString(),
      },
    ];

    if (user?.role === "RestaurantOwner") {
      return [
        ...baseMetrics,
        {
          change: "+0.1",
          changeType: "positive" as const,
          icon: Star,
          title: "Average Rating",
          value: analyticsData.averageRating
            ? analyticsData.averageRating.toFixed(2)
            : "-",
        },
        {
          change: "+2.3%",
          changeType: "positive" as const,
          icon: Users,
          title: "Customer Retention",
          value: analyticsData.customerRetention
            ? `${analyticsData.customerRetention}%`
            : "-",
        },
      ];
    }

    if (user?.role === "FleetOwner") {
      return [
        ...baseMetrics,
        {
          change: "+3.2%",
          changeType: "positive" as const,
          icon: TrendingUp,
          title: "Fleet Utilization",
          value: analyticsData.fleetUtilization
            ? `${analyticsData.fleetUtilization}%`
            : "-",
        },
        {
          change: "+1.8%",
          changeType: "positive" as const,
          icon: Users,
          title: "Driver Efficiency",
          value: analyticsData.driverEfficiency
            ? `${analyticsData.driverEfficiency}%`
            : "-",
        },
      ];
    }

    // CourierOwner
    return [
      ...baseMetrics,
      {
        change: "+0.5%",
        changeType: "positive" as const,
        icon: TrendingUp,
        title: "Delivery Success Rate",
        value: analyticsData.successRate ? `${analyticsData.successRate}%` : "-",
      },
      {
        change: "-2 min",
        changeType: "positive" as const,
        icon: Users,
        title: "Average Delivery Time",
        value: analyticsData.averageDeliveryTime
          ? `${analyticsData.averageDeliveryTime} min`
          : "-",
      },
    ];
  };

  // Pie chart data based on business type
  const getPieChartData = () => {
    if (!analyticsData) return [];
    // Prefer pieCharts returned by API when present
    if (analyticsData.pieCharts && analyticsData.pieCharts.length > 0) {
      return analyticsData.pieCharts.map((c) => ({
        centerValue: c.centerValue,
        segments: c.segments.map((s) => ({
          color: s.color,
          label: s.label,
          percentage: s.percentage,
        })),
        title: c.title,
      }));
    }

    // Fallbacks when API doesn't provide pie chart slices
    if (user?.role === "RestaurantOwner") {
      return [
        {
          centerValue: analyticsData.totalOrders.toString(),
          segments: [
            { color: "#FF6B6B", label: "Delivery", percentage: 62.68 },
            { color: "#4ECDC4", label: "Pickup", percentage: 37.32 },
          ],
          title: "Delivery Option Distribution",
        },
        {
          centerValue: analyticsData.totalOrders.toString(),
          segments: [
            { color: "#45B7D1", label: "Credit Card", percentage: 45.07 },
            { color: "#96CEB4", label: "Cash", percentage: 32.39 },
            { color: "#FFEAA7", label: "Digital Wallet", percentage: 18.31 },
            { color: "#DDA15E", label: "Bank Transfer", percentage: 4.23 },
          ],
          title: "Payment Methods",
        },
        {
          centerValue: analyticsData.averageRating
            ? `${analyticsData.averageRating}★`
            : "-",
          segments: [
            { color: "#00D084", label: "5 Stars", percentage: 55.63 },
            { color: "#DDFF00", label: "4 Stars", percentage: 28.17 },
            { color: "#FF9500", label: "3 Stars", percentage: 12.68 },
            { color: "#FF3B30", label: "Below 3", percentage: 3.52 },
          ],
          title: "Customer Satisfaction",
        },
        {
          centerValue: `${Math.max(...analyticsData.ordersByHour)} orders`,
          segments: [
            { color: "#FF6B6B", label: "11:00-13:00", percentage: 28.87 },
            { color: "#4ECDC4", label: "13:00-15:00", percentage: 25.35 },
            { color: "#45B7D1", label: "18:00-20:00", percentage: 31.69 },
            { color: "#96CEB4", label: "Other Hours", percentage: 14.09 },
          ],
          title: "Peak Hours Distribution",
        },
      ];
    }

    if (user?.role === "FleetOwner") {
      return [
        {
          centerValue: "8 vehicles",
          segments: [
            { color: "#FF6B6B", label: "Sedan", percentage: 37.5 },
            { color: "#4ECDC4", label: "Van", percentage: 37.5 },
            { color: "#45B7D1", label: "Truck", percentage: 25.0 },
          ],
          title: "Vehicle Types",
        },
        {
          centerValue: analyticsData.totalOrders.toString(),
          segments: [
            { color: "#96CEB4", label: "Zone A", percentage: 39.68 },
            { color: "#FFEAA7", label: "Zone B", percentage: 32.27 },
            { color: "#DDA15E", label: "Zone C", percentage: 18.52 },
            { color: "#C1666B", label: "Zone D", percentage: 9.53 },
          ],
          title: "Route Distribution",
        },
        {
          centerValue: analyticsData.driverEfficiency
            ? `${analyticsData.driverEfficiency}%`
            : "-",
          segments: [
            { color: "#00D084", label: "Excellent (95-100%)", percentage: 62.96 },
            { color: "#DDFF00", label: "Good (85-94%)", percentage: 25.93 },
            { color: "#FF9500", label: "Average (75-84%)", percentage: 8.47 },
            { color: "#FF3B30", label: "Below Average", percentage: 2.64 },
          ],
          title: "Driver Performance",
        },
        {
          centerValue: "8 vehicles",
          segments: [
            { color: "#00D084", label: "Active", percentage: 87.5 },
            { color: "#FF9500", label: "In Maintenance", percentage: 12.5 },
          ],
          title: "Maintenance Status",
        },
      ];
    }

    // CourierOwner fallback
    return [
      {
        centerValue: analyticsData.totalOrders.toString(),
        segments: [
          { color: "#FF6B6B", label: "Standard", percentage: 60.94 },
          { color: "#4ECDC4", label: "Express", percentage: 39.06 },
        ],
        title: "Delivery Types",
      },
      {
        centerValue: analyticsData.totalOrders.toString(),
        segments: [
          { color: "#45B7D1", label: "Small", percentage: 35.16 },
          { color: "#96CEB4", label: "Medium", percentage: 42.97 },
          { color: "#FFEAA7", label: "Large", percentage: 18.75 },
          { color: "#DDA15E", label: "Extra Large", percentage: 3.12 },
        ],
        title: "Package Sizes",
      },
      {
        centerValue: analyticsData.successRate
          ? `${analyticsData.successRate}%`
          : "-",
        segments: [
          { color: "#00D084", label: "Completed", percentage: 96.88 },
          { color: "#FF9500", label: "Pending", percentage: 2.34 },
          { color: "#FF3B30", label: "Failed", percentage: 0.78 },
        ],
        title: "Delivery Success",
      },
      {
        centerValue: analyticsData.totalOrders.toString(),
        segments: [
          { color: "#FF6B6B", label: "Downtown", percentage: 38.28 },
          { color: "#4ECDC4", label: "Suburbs", percentage: 32.81 },
          { color: "#45B7D1", label: "Industrial", percentage: 18.36 },
          { color: "#96CEB4", label: "Remote", percentage: 10.55 },
        ],
        title: "Service Areas",
      },
    ];
  };

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 dark:border-gray-700 dark:border-t-blue-400"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Loading analytics...
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-400">
          <p>{error}</p>
        </div>
      )}

      {!loading && analyticsData && (
        <>
          <div className="mb-4 flex flex-col items-start justify-between gap-4 sm:mb-6 sm:flex-row sm:items-center">
            <div>
              <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl">
                Business Analytics
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                Detailed insights and performance metrics for your business.
              </p>
            </div>

            {/* Time Range Selector */}
            <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
              {(["week", "month", "year"] as const).map((range) => (
                <button
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:px-4 sm:py-2 sm:text-sm ${
                    timeRange === range
                      ? "bg-white text-blue-600 shadow-sm dark:bg-gray-800 dark:text-blue-400"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                  }`}
                  key={range}
                  onClick={() => setTimeRange(range)}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
            {getBusinessTypeMetrics().map((metric, index) => (
              <div
                className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6"
                key={index}
              >
                <div className="mb-3 flex items-center justify-between sm:mb-4">
                  <div className="rounded-full bg-blue-100 p-2 text-blue-600 dark:bg-blue-900 dark:text-blue-400 sm:p-3">
                    <metric.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <span
                    className={`text-xs font-medium sm:text-sm ${
                      metric.changeType === "positive"
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {metric.change}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                    {metric.title}
                  </p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                    {metric.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pie Charts Section */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
            {getPieChartData().map((chart, chartIndex) => (
              <div
                className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6"
                key={chartIndex}
              >
                <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                  {chart.title}
                </h3>
                <div className="flex items-center justify-center">
                  <div className="relative h-28 w-28 sm:h-32 sm:w-32">
                    <svg
                      className="h-28 w-28 -rotate-90 transform sm:h-32 sm:w-32"
                      viewBox="0 0 42 42"
                    >
                      {chart.segments.map((segment, segmentIndex) => {
                        const previousPercentages = chart.segments
                          .slice(0, segmentIndex)
                          .reduce((sum, s) => sum + s.percentage, 0);

                        return (
                          <circle
                            cx="21"
                            cy="21"
                            fill="transparent"
                            key={segmentIndex}
                            r="15.915"
                            stroke={segment.color}
                            strokeDasharray={`${segment.percentage} ${100 - segment.percentage}`}
                            strokeDashoffset={25 - previousPercentages}
                            strokeWidth="3"
                          />
                        );
                      })}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-base font-bold text-gray-900 dark:text-gray-100 sm:text-lg">
                        {chart.centerValue}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2">
                  {chart.segments.map((segment, segmentIndex) => (
                    <div
                      className="flex items-center justify-between"
                      key={segmentIndex}
                    >
                      <div className="flex items-center">
                        <div
                          className="mr-2 h-2.5 w-2.5 rounded-full sm:h-3 sm:w-3"
                          style={{ backgroundColor: segment.color }}
                        ></div>
                        <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                          {segment.label}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 sm:text-sm">
                        {segment.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Revenue Chart */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Monthly Revenue
              </h3>
              <div className="flex h-48 items-end justify-between space-x-1 sm:h-64 sm:space-x-2">
                {analyticsData.monthlyRevenue.map((revenue, index) => (
                  <div
                    className="flex flex-1 flex-col items-center"
                    key={index}
                  >
                    <div
                      className="w-full max-w-[32px] rounded-t-sm bg-gradient-to-t from-blue-500 to-blue-400"
                      style={{
                        height: `${(revenue / Math.max(...analyticsData.monthlyRevenue)) * 160}px`,
                      }}
                    ></div>
                    <span className="mt-1 text-[9px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                      {
                        [
                          "Jan",
                          "Feb",
                          "Mar",
                          "Apr",
                          "May",
                          "Jun",
                          "Jul",
                          "Aug",
                          "Sep",
                          "Oct",
                          "Nov",
                          "Dec",
                        ][index]
                      }
                    </span>
                    <span className="text-[9px] font-medium text-gray-700 dark:text-gray-300 sm:text-xs">
                      {formatCurrency(revenue)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Orders by Hour */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Orders by Hour
              </h3>
              <div className="flex h-48 items-end justify-between space-x-0.5 sm:h-64 sm:space-x-1">
                {analyticsData.ordersByHour.map((orders, index) => (
                  <div
                    className="flex flex-1 flex-col items-center"
                    key={index}
                  >
                    <div
                      className="w-full max-w-[12px] rounded-t-sm bg-gradient-to-t from-green-500 to-green-400"
                      style={{
                        height: `${(orders / Math.max(...analyticsData.ordersByHour)) * 160}px`,
                      }}
                    ></div>
                    {index % 4 === 0 && (
                      <span className="mt-1 text-[9px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                        {index}h
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Products/Performance Table */}
          {user?.role === "RestaurantOwner" && (
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Top Performing Items
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 sm:pb-3 sm:text-sm">
                        Item Name
                      </th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 sm:pb-3 sm:text-sm">
                        Revenue
                      </th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400 sm:pb-3 sm:text-sm">
                        Orders
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.topProducts.map((product, index) => (
                      <tr
                        className="border-b border-gray-100 dark:border-gray-700"
                        key={index}
                      >
                        <td className="py-2 text-sm text-gray-900 dark:text-gray-100 sm:py-3 sm:text-base">
                          {product.name}
                        </td>
                        <td className="py-2 text-right text-sm text-gray-900 dark:text-gray-100 sm:py-3 sm:text-base">
                          {formatCurrency(product.revenue)}
                        </td>
                        <td className="py-2 text-right text-sm text-gray-900 dark:text-gray-100 sm:py-3 sm:text-base">
                          {product.orders}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BusinessAnalytics;
